package com.downdogapp.client.singleton

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.api.*
import com.downdogapp.client.api.Language.JAPANESE
import com.downdogapp.client.api.Language.KOREAN
import com.downdogapp.client.api.Language.MANDARIN
import com.downdogapp.client.api.LinkType.PUSH_NOTIFICATION
import com.downdogapp.client.controllers.AlertViewController
import com.downdogapp.client.controllers.PurchaseViewController
import com.downdogapp.client.controllers.YearlyMonthlyPricePair
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.resources.Images
import kotlin.reflect.KClass

object App : AppHelperInterface by AppHelper, LogEmitter {

  var cred: String? = UserPrefs[Key.Cred]
    set(value) {
      UserPrefs[Key.Cred] = value
      Logger.onCredChanged()
      field = value
    }

  val pricesMap: Map<String, YearlyMonthlyPricePair> = mutableMapOf()

  fun incrementBackgroundImage() {
    UserPrefs[Key.StartNumViews] = 1 + (UserPrefs[Key.StartNumViews] ?: -1)
  }

  private val IMAGES = listOf(
    Images.START_1,
    Images.START_2,
    Images.START_3,
    Images.START_4,
    Images.START_5,
    Images.START_6,
    Images.START_7,
    Images.START_8
  )

  fun getBackgroundImage() =
    if (Strings.language in setOf(MANDARIN, JAPANESE, KOREAN))
      Images.ASIAN_BACKGROUND
    else
      IMAGES[(UserPrefs[Key.StartNumViews] ?: 0) % IMAGES.size]

  fun fetchManifest(callback: () -> Unit) =
    fetchManifest(true, null, null, callback)

  fun fetchManifest(loadStoredOnFailure: Boolean = true,
                    oldCred: String? = null,
                    signInReason: SignInReason? = null,
                    callback: () -> Unit) {
    Network.postAndSave(ManifestRequest(oldCred = oldCred, signInReason = signInReason), Key.Manifest) { json ->
      val manifest =
        json?.let { Manifest.fromJson(it) }
        ?: if (loadStoredOnFailure)
          UserPrefs[Key.Manifest]?.let {
            logEvent("loading_stored_manifest")
            Manifest.fromJson(it)
          }
        else
          null

      if (manifest == null) {
        alertNoInternet {
          exit()
        }
      } else {
        MANIFEST = manifest
        MANIFEST.cred?.let {
          cred = it
        }
        if (MANIFEST.requiredUpdateMessage == null) {
          SequenceSettings.onManifestFetched()
          MediaSampleManager.onManifestFetched()
        }
        val newProductIds =
          MANIFEST
            .membershipConfig
            .products
            .map { it.productId }
            .filterToSet { it !in pricesMap }
        if (newProductIds.isNotEmpty()) {
          getPrices(newProductIds) {
            (pricesMap as MutableMap).putAll(it)
          }
        }
        HistoryUtil.fetchHistory()
        getViewController(StartViewController::class)?.refreshView()
        callback()
      }
    }
  }

  fun alertNoInternet(message: String = Strings.NO_INTERNET_MESSAGE,
                      callback: (() -> Unit)? = null) =
    alert(
      title = Strings.CONNECTION_PROBLEM,
      message = message,
      callback = callback
    )

  fun alert(title: String? = null,
            message: String?,
            callback: (() -> Unit)? = null) {
    alert(
      title = title,
      message = message ?: "",
      positiveButton = AlertAction(Strings.OK) {
        callback?.invoke()
      }
    )
  }

  fun alert(title: String? = null,
            message: String,
            negativeButton: AlertAction? = null,
            positiveButton: AlertAction) =
    App.pushViewController(
      AlertViewController(
        title = title,
        message = message,
        negativeButton = negativeButton,
        positiveButton = positiveButton
      )
    )

  fun maybeOpenNotificationUrl(url: String) {
    if (getViewController(PlaybackViewController::class) == null) {
      Network.post(RecordLinkClickedRequest(id = url, linkType = PUSH_NOTIFICATION))
      AppHelper.openUrl(url)
    }
  }

  fun handleSharedSequence(sequenceId: String) {
    val startVC = getViewController(StartViewController::class)
    if (startVC != null && !MANIFEST.displayLoginOnStart) { // short circuits if manifest not initialized
      // If StartVC exists (and no login), unwind and prompt immediately
      val sequenceVC = getViewController(PlaybackViewController::class)
      if (sequenceVC != null) {
        sequenceVC.exitToStartScreen()
      } else {
        unwindToViewController(StartViewController::class)
      }
      startVC.promptLinkedSequence(sequenceId)
    } else { // Otherwise, put sequence in UserPrefs
      UserPrefs[Key.LinkedSequenceId] = sequenceId
    }
  }

  @Suppress("UNCHECKED_CAST")
  fun <T : ViewController> getViewController(kClass: KClass<T>): T? =
    getViewController(kClass.simpleName!!) as? T

  fun <T : ViewController> unwindToViewController(kClass: KClass<T>) {
    unwindToViewController(kClass.simpleName!!)
  }

  fun shareSequence(sequenceId: String) {
    App.showShareDialog(url = "${MANIFEST.shareUrl}?sequenceId=$sequenceId")
  }

  fun goToNewSequence() {
    goToSequence(null)
  }

  fun goToExistingSequence(sequenceId: String) {
    goToSequence(sequenceId)
  }
}

interface AppHelperInterface : LogEmitter {

  val appType: AppType
  val platform: Platform
  val debug: Boolean
  val aspectRatio: Double?

  fun popViewController(callback: () -> Unit = {})
  fun pushViewController(vc: ViewController)
  fun unwindToViewController(name: String, callback: () -> Unit = {})
  fun getViewController(name: String): ViewController?

  fun runAfter(duration: Duration, onlyIfActive: Boolean = false, runnable: () -> Unit)
  fun runInMain(runnable: () -> Unit)
  fun openFirstValidUrl(urls: List<String>)
  fun openUrl(url: String)
  fun goToPurchase(onPop: () -> Unit = {})
  fun goToSequence(sequenceId: String?)
  fun exit()
  fun logoutAccounts()
  fun showShareDialog(url: String)

  // web only
  fun isBrowserSafari(): Boolean
  fun isBrowserPhone(): Boolean
  fun goHomeOrOpenApp()
  fun pushWebHistory(url: String, hash: String)
  fun payWithCreditCard(purchaseViewController: PurchaseViewController)

  // mobile only
  fun isBackgrounded(): Boolean
  fun getPrices(productIds: Set<String>, callback: (Map<String, YearlyMonthlyPricePair>) -> Unit)
  fun restorePurchases()
  fun payInApp(productId: String, purchaseViewController: PurchaseViewController)
  fun preventSleep(value: Boolean)
  fun requestReview()
  fun deactivatePlaybackSession()
  fun setupPlaybackControls(
    onPlay: (() -> Unit)?,
    onPause: (() -> Unit)?,
    onChangePlaybackPosition: ((position: Double) -> Unit)?,
    onSkipForward: (() -> Unit)?,
    onSkipBackward: (() -> Unit)?
  )

  // android only
  fun closeKeyboard()
  fun updateFullscreen()

  // ios only
  fun activatePlaybackSession(duckOthers: Boolean)
  fun setPlaybackState(isPlaying: Boolean, elapsedTime: Duration, playbackDuration: Duration)
  fun requestNotificationsAuthorization(callback: () -> Unit = {}) // other platforms just execute the callback
}

data class AlertAction(val title: String?, val callback: () -> Unit = {})

expect object AppHelper : AppHelperInterface
